import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Subscription } from 'rxjs';

import { QuoteInfoService } from '../services/quote-info.service';
import { ApplicationStateService } from '../services/application-state.service';
import { OptionsService } from '../services/options.service';
import { AddCustomOptionService } from '../services/add-custom-option.service';
import { LoadingSpinnerService } from '../services/loading-spinner.service';
import { OptionsAsideService } from '../services/options-aside.service';
import { CurrentUserService } from '../services/current-user.service';
import { HttpResponseService } from '../services/httpresponse-service';
import { BrowserWidthService } from '../services/browser-width.service';
import { QuoteSummaryService } from '../services/quote-summary.service';
import { Adjustment } from '../model/adjustment';
import { SelectedOption } from '../model/selected-option';
import { MasterOptionsModel } from '../model/master-options-model';
import { SubOption } from '../model/suboption';
import { SortCustomOptionsPipe } from '../pipes/sort-custom-options.pipe';

@Component({
    template: require('./option.component.html'),
    styles: [require('./option.component.scss')],
    providers: [ SortCustomOptionsPipe ]
})
export class OptionComponent implements OnInit, OnDestroy {
    currentPricingView: number;
    quoteId: number;
    modelId: number;
    categoryId: number;

    modifiedBy: string;
    searchTerm: string;

    quoteAdjustment: Adjustment;

    incrementQuantitySelectedOption: SelectedOption;
    subscription: Subscription = new Subscription();

    subscribe: boolean = true;
    enableSave: boolean = true;
    displayNoMatches: boolean;
    facilityUser: boolean = true;
    faciltiyUserWithoutApprover: boolean = false;
    facilityUserWithApprover: boolean = false;
    initialPageLoad: boolean = true;

    // added/used for performance refactor
    loadingMasterOptions: boolean = false;
    masterOptionModel: MasterOptionsModel = new MasterOptionsModel;
    currentSelectedOptions: SelectedOption[];
    allSelectedOptions: SelectedOption[];
    customOptions: SelectedOption[];
    mappedOptions: SelectedOption[];

    constructor(
        public quoteInfoService: QuoteInfoService,
        public applicationStateService: ApplicationStateService,
        public optionsService: OptionsService,
        public addCustomOptionService: AddCustomOptionService,
        public route: ActivatedRoute,
        public spinner: LoadingSpinnerService,
        public optionsAsideService: OptionsAsideService,
        public currentUserService: CurrentUserService,
        public httpResponseService: HttpResponseService,
        public browserWidthService: BrowserWidthService,
        public quoteSummaryService: QuoteSummaryService,
        private sortCustomOptionsPipe: SortCustomOptionsPipe
    ) { }

    ngOnInit() {
        const pageLoadSub = this.optionsService.initialOptionPageLoad$.subscribe(x => { this.initialPageLoad = x; });
        this.subscription.add(pageLoadSub);

        const routeParamsSub = this.route.params.subscribe(x => {
            if (x['categoryId'] !== undefined) {
                this.categoryId = +x['categoryId'];
                this.quoteInfoService.categoryId$.next(this.categoryId);
            }
        });
        this.subscription.add(routeParamsSub);

        const currentUserSub = this.currentUserService.currentUser$.subscribe(cu => {
            this.modifiedBy = cu.cmhUserId + '-' + cu.firstName + ' ' + cu.lastName;

            if (cu.isPlantUser) {
                cu.roles.includes('MyCustomHomes_Quote_Approver') ? this.setUser(false, false, true) : this.setUser(false, true, false);
            } else {
                this.setUser(true, false, false);
            }
        });
        this.subscription.add(currentUserSub);

        const searchSub = this.optionsService.searchTerm$.asObservable().subscribe(x => { this.searchTerm = x });
        this.subscription.add(searchSub);

        const noMatchesSub = this.optionsService.noMatches.asObservable().debounceTime(1).subscribe(x => {
            if (x.includes('custom') && x.includes('normal')) {
                this.displayNoMatches = true;
            } else {
                this.displayNoMatches = false;
            }
        });
        this.subscription.add(noMatchesSub);

        const includeExcludeModalSub = this.optionsAsideService.includeExcludeModalStatus.asObservable().subscribe(x => {
            if (x === false) {
                this.optionsService.setMappedSelectedOptions(
                    this.currentSelectedOptions, this.customOptions, this.modifiedBy, this.quoteId
                );
            }

        });
        this.subscription.add(includeExcludeModalSub);

        const infoAsideSub = this.optionsAsideService.informationAsideStatus.asObservable().subscribe(x => {
            if (x === false) {
                this.optionsService.setMappedSelectedOptions(this.allSelectedOptions, this.customOptions, this.modifiedBy, this.quoteId);
            }
        });
        this.subscription.add(infoAsideSub);

        const resetModalSub = this.optionsService.resetSelectedOptions$.asObservable().subscribe(x => {
            if (x === true) {
                this.resetSelectedOptions();
            }
        });
        this.subscription.add(resetModalSub);

        const customOptionsFromDB = this.optionsService.customOptionsfromDB$.asObservable().subscribe(x => {
            // replace custom options in the all selected option list with those from DB
            if (x !== null && x !== undefined && x.length > 0) {
                if (this.quoteId > 0) {
                    this.quoteSummaryService.getPricingHeader(this.quoteId)
                        .subscribe(
                            (data => { }),
                            (error => { this.httpResponseService.httpErrorResponse$.next(error); })
                        );
                }

                if (this.allSelectedOptions !== undefined && this.allSelectedOptions !== null && this.allSelectedOptions.length > 0) {
                    // save custom options from other categories
                    let customOptions = this.allSelectedOptions.filter(
                        aso => aso.IsCustomOption === true && aso.HomeOptionsCategoryId !== +this.categoryId
                    );
                    this.allSelectedOptions = this.allSelectedOptions.filter(aso => aso.IsCustomOption === false);
                    x.forEach(co => {
                        if (
                            this.customOptions.filter(cco => cco.SelectedOptionId === co.SelectedOptionId &&
                            cco.WasUIChanged === true).length > 0
                        ) {
                            this.allSelectedOptions.push(...this.customOptions.filter(
                                cco => cco.SelectedOptionId === co.SelectedOptionId && cco.WasUIChanged === true
                            ));
                        } else {
                            if (co.UIUnitPrice === null) { co.UIUnitPrice = co.UnitPrice.toFixed(2); }
                            co.AdjustedTotalPrice = Number(co.UIUnitPrice) * co.Quantity;
                            co.HomeOptionsCategoryId = +this.categoryId;
                            this.allSelectedOptions.push(co);
                        }
                    });
                    customOptions.forEach(co => { this.allSelectedOptions.push(co); });

                    this.masterOptionModel.AllSelectedOptions = this.allSelectedOptions;
                    this.customOptions = this.allSelectedOptions.filter(
                        aso => aso.IsCustomOption === true && aso.HomeOptionsCategoryId === +this.categoryId
                    );
                    this.sortSelectedOptions();
                    this.sortCustomOptions();
                }
            }
        });
        this.subscription.add(customOptionsFromDB);

        const categoryIdSub = this.quoteInfoService.categoryId$.subscribe(x => {
            this.spinner.showSpinner();
            x = (x === undefined || x === null) ? undefined : x;

            if (x !== undefined) {
                this.categoryId = +x;

                this.quoteInfoService.quote$.asObservable().takeWhile(() => this.subscribe).subscribe(y => {
                    this.quoteId = y.quoteId;
                    this.modelId = y.modelId;
                    this.quoteInfoService.currentPricingView$.asObservable().takeWhile(() => this.subscribe).subscribe(pricingView => {
                        pricingView = (pricingView === undefined || pricingView === null) ? undefined : pricingView;
                        this.currentPricingView = pricingView;

                        if (this.quoteId && this.modelId && this.categoryId && this.currentPricingView) {
                            this.spinner.showSpinner();
                            if (!this.initialPageLoad) {
                                this.quoteAdjustment = this.quoteInfoService.adjustment$.value;

                                if (
                                    this.masterOptionModel.AllSelectedOptions !== undefined &&
                                    this.masterOptionModel.AllSelectedOptions.length > 0
                                ) {
                                    this.currentSelectedOptions = this.masterOptionModel.AllSelectedOptions.filter(
                                        aso => aso.HomeOptionsCategoryId === +this.categoryId && aso.IsCustomOption !== true
                                    );
                                    this.optionsService.selectedOptions$.next(this.currentSelectedOptions);

                                    this.customOptions = this.allSelectedOptions.filter(
                                        selectedOption => selectedOption.IsCustomOption === true &&
                                            selectedOption.HomeOptionsCategoryId === +this.categoryId
                                    );
                                }
                                this.sortSelectedOptions();
                                this.sortCustomOptions();

                                this.spinner.hideSpinner();
                            } else {
                                if (!this.loadingMasterOptions) {
                                    this.getMasterOptions();
                                }
                            }
                        }
                    });
                });
            }

        });
        this.subscription.add(categoryIdSub);

        const ieUpdateAllSelectedOptionsSub = this.optionsService.includeExcludeAllSelectedOptions$.asObservable().subscribe(x => {
            x = x === undefined || x === null ? new Array<SelectedOption>() : x;
            if (x.length > 0) {
                if (this.masterOptionModel !== undefined) {
                    this.masterOptionModel.AllSelectedOptions = x;
                }
            }
        });
        this.subscription.add(ieUpdateAllSelectedOptionsSub);

        const currentPricingViewSub = this.quoteInfoService.currentPricingView$.subscribe(x => {
            this.currentPricingView = x;
            this.masterOptionModel = this.masterOptionModel === undefined || this.masterOptionModel === null ?
                new MasterOptionsModel() :
                this.masterOptionModel;

            this.masterOptionModel = this.optionsService.calculatePricingRefactor(
                this.masterOptionModel,
                this.quoteAdjustment,
                this.currentPricingView
            );
        });
        this.subscription.add(currentPricingViewSub);

    }

    ngOnDestroy() {
        this.subscribe = false;
        this.customOptions = undefined;
        this.currentSelectedOptions = undefined;
        this.subscription.unsubscribe();
    }

    getMasterOptions() {
        this.spinner.showSpinner();
        this.loadingMasterOptions = true;
        const getMasterOptionsSub = this.optionsService.getMasterOptions(
            this.quoteId,
            this.modelId,
            +this.categoryId,
            this.currentPricingView
            ).subscribe(
                (x => {
                    this.masterOptionModel = x;
                    if (x.AllSelectedOptions !== undefined && x.AllSelectedOptions !== null && x.AllSelectedOptions.length > 0) {
                        this.optionsService.AdminOptionsExist$.next(x.AllSelectedOptions[0].AdminOptionsExist);
                    }

                    x.AllSelectedOptions.forEach(aso => { aso.InvalidSubOption = false; });
                    this.allSelectedOptions = x.AllSelectedOptions;
                    this.currentSelectedOptions = this.allSelectedOptions.filter(
                        aso => aso.HomeOptionsCategoryId === +this.categoryId && aso.IsCustomOption !== true
                    );
                    this.customOptions = this.allSelectedOptions.filter(
                        selectedOption => selectedOption.HomeOptionsCategoryId === +this.categoryId &&
                            selectedOption.IsCustomOption === true
                    );
                    this.customOptions.forEach(co => { co.Visible = true; });

                    this.optionsService.currentSelectedOptions$.next(this.currentSelectedOptions);
                    this.optionsService.allSelectedOptions$.next(this.allSelectedOptions);
                }),
                (error => {
                    this.spinner.hideSpinner();
                    this.httpResponseService.httpErrorResponse$.next(error);
                    this.loadingMasterOptions = false;
                }),
                (() => {
                    this.spinner.showSpinner();
                    this.sortSelectedOptions();
                    this.sortCustomOptions();
                    this.quoteAdjustment = this.quoteInfoService.adjustment$.value;
                    this.spinner.hideSpinner();
                    this.optionsService.initialOptionPageLoad$.next(false);
                    this.loadingMasterOptions = false;
                })
            );
        this.subscription.add(getMasterOptionsSub);
    }

    /// Resets the page's SelectedOptions back to factory default, then reloads the page.
    resetSelectedOptions() {
        this.spinner.showSpinner();
        this.loadingMasterOptions = true;
        let options = this.currentSelectedOptions;
        const resetSOSub = this.optionsService.resetSelectedOptions(this.quoteId, +this.categoryId, this.currentPricingView).subscribe(
            (x => {
                this.masterOptionModel = x;
                x.AllSelectedOptions.forEach(aso => { aso.InvalidSubOption = false; });
                this.allSelectedOptions = x.AllSelectedOptions;
                this.currentSelectedOptions = this.allSelectedOptions.filter(
                    aso => aso.HomeOptionsCategoryId === +this.categoryId && aso.IsCustomOption !== true
                );
                this.customOptions = this.allSelectedOptions.filter(
                    selectedOption => selectedOption.HomeOptionsCategoryId === +this.categoryId && selectedOption.IsCustomOption === true
                );
                this.customOptions.forEach(co => { co.Visible = true; });

                this.optionsService.currentSelectedOptions$.next(this.currentSelectedOptions);
                this.optionsService.allSelectedOptions$.next(this.allSelectedOptions);

                if (this.quoteId > 0) {
                    this.quoteSummaryService.getPricingHeader(this.quoteId)
                        .subscribe(
                            (data => { }),
                            (error => {
                                this.httpResponseService.httpErrorResponse$.next(error);
                            }),
                    );
                }

            }),
            (error => {
                this.spinner.hideSpinner();
                this.httpResponseService.httpErrorResponse$.next(error);
            }),
            (() => {
                this.spinner.showSpinner();
                this.sortSelectedOptions();
                this.sortCustomOptions();
                this.quoteAdjustment = this.quoteInfoService.adjustment$.value;
                this.spinner.hideSpinner();
                this.optionsService.initialOptionPageLoad$.next(false);
                this.loadingMasterOptions = false;
            })
        );
        this.subscription.add(resetSOSub);
    }

    /// Sorts only CustomOptions
    private sortCustomOptions() {
        this.customOptions = this.sortCustomOptionsPipe.transform(this.customOptions);
    }

    /// Sorts SelectedOptions
    sortSelectedOptions() {
        this.currentSelectedOptions.sort((a, b) => {
            return b.Quantity - a.Quantity || b.SubOptionCount - a.SubOptionCount;
        });
    }

    /// Increments an Option by 1
    incrementQuantity(option: SelectedOption, isCustom: boolean) {
        this.optionsService.setMappedSelectedOptions(this.currentSelectedOptions, this.customOptions, this.modifiedBy, this.quoteId);
        this.optionsService.optionsChanged$.next(true);

        if (isCustom) {
            let objIndex = this.customOptions.findIndex(ccO => ccO.SelectedOptionId === option.SelectedOptionId);
            if (objIndex >= 0) {
                if (Number(this.customOptions[objIndex].UIUnitPrice) >= 0) {
                    this.customOptions[objIndex].WasUIChanged = true;
                    this.customOptions[objIndex].Quantity = ((this.customOptions[objIndex].Quantity + 1) >= 9999) ?
                        9999 :
                        (this.customOptions[objIndex].Quantity + 1);
                    this.customOptions[objIndex].AdjustedTotalPrice = (
                        this.customOptions[objIndex].Quantity * Number(this.customOptions[objIndex].UIUnitPrice)
                    );
                }
            }

        } else {
            let objIndex = this.currentSelectedOptions.findIndex(ccO => ccO.HomeOptionsOptionId === option.OptionId);
            if (objIndex >= 0) {
                this.currentSelectedOptions[objIndex].WasUIChanged = true;
                if (this.currentSelectedOptions[objIndex].SubOptionCount === 1) {
                    this.currentSelectedOptions[objIndex].HomeOptionsSubOptionId =
                        this.currentSelectedOptions[objIndex].SelectedOptionSubOptions[0].SubOptionId;
                }
                if (
                    this.currentSelectedOptions[objIndex].IncludeExclude &&
                    this.currentSelectedOptions[objIndex].IncludeExclude.length > 0
                ) {
                    if (this.currentSelectedOptions[objIndex].MaxQuantity === 0) {
                    } else {
                        if (this.currentSelectedOptions[objIndex].Quantity > this.currentSelectedOptions[objIndex].MaxQuantity) {
                            this.currentSelectedOptions[objIndex].Quantity = this.currentSelectedOptions[objIndex].MaxQuantity;
                        }
                    }

                    if (this.currentSelectedOptions[objIndex].Quantity === 0) {
                        this.incrementQuantitySelectedOption = this.currentSelectedOptions[objIndex];
                        this.optionsAsideService.openIncludeExcludeModal(this.currentSelectedOptions, this.incrementQuantitySelectedOption);
                    } else {
                        this.currentSelectedOptions[objIndex].Quantity = ((this.currentSelectedOptions[objIndex].Quantity + 1) >= 9999) ?
                            9999 :
                            (this.currentSelectedOptions[objIndex].Quantity + 1);
                        if (
                            this.currentSelectedOptions[objIndex].MaxQuantity > 0 &&
                            this.currentSelectedOptions[objIndex].Quantity > this.currentSelectedOptions[objIndex].MaxQuantity
                        ) {
                            this.currentSelectedOptions[objIndex].Quantity = this.currentSelectedOptions[objIndex].MaxQuantity;
                        }
                        this.currentSelectedOptions[objIndex].AdjustedTotalPrice = (
                            this.currentSelectedOptions[objIndex].Quantity * Number(this.currentSelectedOptions[objIndex].UIUnitPrice)
                        );
                    }
                } else {
                    if (Number(this.currentSelectedOptions[objIndex].UIUnitPrice) >= 0) {
                        this.currentSelectedOptions[objIndex].Quantity = ((this.currentSelectedOptions[objIndex].Quantity + 1) >= 9999) ?
                            9999 :
                            (this.currentSelectedOptions[objIndex].Quantity + 1);
                        this.currentSelectedOptions[objIndex].AdjustedTotalPrice = (
                            this.currentSelectedOptions[objIndex].Quantity * Number(this.currentSelectedOptions[objIndex].UIUnitPrice)
                        );
                    } else {
                        this.currentSelectedOptions[objIndex].Quantity = 1;
                        this.currentSelectedOptions[objIndex].AdjustedTotalPrice = (
                            this.currentSelectedOptions[objIndex].Quantity * Number(this.currentSelectedOptions[objIndex].UIUnitPrice)
                        );
                    }
                }

                if (this.currentSelectedOptions[objIndex].Price === 0) {
                    this.currentSelectedOptions[objIndex].Price = Number(this.currentSelectedOptions[objIndex].UIUnitPrice);
                }
                // check if unselected option and if so set error
                this.currentSelectedOptions[objIndex].InvalidSubOption = false;
            }
        }
        this.optionsService.allSelectedOptions$.next(this.allSelectedOptions);
        this.optionsService.setMappedSelectedOptions(this.currentSelectedOptions, this.customOptions, this.modifiedBy, this.quoteId);
        this.optionsService.markOptionChangedByFacility(option);
    }

    /// Decrements an Option by 1
    decrementQuantity(option: SelectedOption, isCustom: boolean) {
        this.optionsService.optionsChanged$.next(true);

        if (isCustom) {
            let objIndex = this.customOptions.findIndex(co => co.SelectedOptionId === option.SelectedOptionId);
            if (objIndex >= 0) {
                this.customOptions[objIndex].Quantity = ((this.customOptions[objIndex].Quantity - 1) <= 0) ?
                    0 :
                    (this.customOptions[objIndex].Quantity - 1);
                this.customOptions[objIndex].AdjustedTotalPrice = (
                    this.customOptions[objIndex].Quantity * Number(this.customOptions[objIndex].UIUnitPrice)
                );
                this.customOptions[objIndex].WasUIChanged = true;
            }

        } else {
            let objIndex = this.currentSelectedOptions.findIndex(csO => csO.HomeOptionsOptionId === option.OptionId);
            if (objIndex >= 0) {
                this.currentSelectedOptions[objIndex].Quantity = ((this.currentSelectedOptions[objIndex].Quantity - 1) <= 0) ?
                    0 :
                    (this.currentSelectedOptions[objIndex].Quantity - 1);
                this.currentSelectedOptions[objIndex].AdjustedTotalPrice = (
                    this.currentSelectedOptions[objIndex].Quantity * Number(this.currentSelectedOptions[objIndex].UIUnitPrice)
                );
                this.currentSelectedOptions[objIndex].WasUIChanged = true;
                if (this.currentSelectedOptions[objIndex].SubOptionCount === 0) {
                    this.currentSelectedOptions[objIndex].InvalidSubOption = false;
                }

                // check if unselected option and if so set error
                this.currentSelectedOptions[objIndex].InvalidSubOption = false;

                // now updated the object in the master list
                let masterIndex = this.allSelectedOptions.findIndex(mo => mo.HomeOptionsOptionId === option.OptionId);
                if (masterIndex >= 0) {
                    this.allSelectedOptions[masterIndex].Quantity = this.currentSelectedOptions[objIndex].Quantity;
                    this.allSelectedOptions[masterIndex].AdjustedTotalPrice = this.currentSelectedOptions[objIndex].AdjustedTotalPrice;
                    this.allSelectedOptions[masterIndex].WasChanged = this.currentSelectedOptions[objIndex].WasChanged;
                    this.allSelectedOptions[masterIndex].WasUIChanged = this.currentSelectedOptions[objIndex].WasUIChanged;
                    this.allSelectedOptions[masterIndex].InvalidSubOption = this.currentSelectedOptions[objIndex].InvalidSubOption;
                }

            }
        }
        this.optionsService.setMappedSelectedOptions(this.currentSelectedOptions, this.customOptions, this.modifiedBy, this.quoteId);
        this.optionsService.allSelectedOptions$.next(this.allSelectedOptions);
        this.optionsService.markOptionChangedByFacility(option);
    }


    filterHiddenSubOptions(subOptions: SubOption[]): SubOption[] {
        if (subOptions.length === 0) {
            return subOptions
        }

        try {
            const optionId = subOptions[0].OptionId;
            const subOptionOverrides = this.masterOptionModel.AllNormalOptions.find(
                normalOption => {
                    if(!normalOption.OptionId) { return false; }
                    return normalOption.OptionId === optionId;
                }
            ).SubOptions;

            return subOptions.filter(subOption => {
                // If the suboption does not have an admin option, then it was never overridden, and we should show it
                const override = subOptionOverrides.find(
                    s => s.SubOptionId === subOption.SubOptionId && s.AdminOption !== undefined && s.AdminOption !== null
                )
                if(override) {
                    // If the override has an AdminOptionId of 0, it's actually a dummy because the
                    // suboption was never overridden, and we should show the suboption
                    return override.AdminOption.Visible || override.AdminOption.AdminOptionId === 0
                }
                return true;
            });
        } catch (e) {
            console.log('something failed when filtering suboptions');
            console.log(e)
            console.log(subOptions);
            return subOptions;
        }
    }

    /// Changes the SubOption
    changeSubOption(selectedOption: SelectedOption) {
        this.optionsService.optionsChanged$.next(true);
        let subOption = selectedOption.SelectedOptionSubOptions.find(x => x.SubOptionId === selectedOption.SelectedSubOptionId);
        selectedOption.SelectedSubOptionDesc = subOption.OptionDescription;
        selectedOption.HomeOptionsSubOptionId = subOption.SubOptionId;
        selectedOption.OptionDesc = subOption.OptionDescription;
        selectedOption.OptionDescription = subOption.OptionDescription;
        selectedOption.InvalidSubOption = false;
        selectedOption.WasChanged = true;
        selectedOption.WasUIChanged = true;

        if (selectedOption.Quantity === 0 && selectedOption.SelectedSubOptionId > 0) {
            this.incrementQuantity(selectedOption, false);
        }

        this.optionsService.allSelectedOptions$.next(this.allSelectedOptions);
        this.optionsService.setMappedSelectedOptions(this.currentSelectedOptions, this.customOptions, this.modifiedBy, this.quoteId);
        this.optionsService.markOptionChangedByFacility(selectedOption);
    }

    clearUnitPrice(item, isCustom: boolean) {
        if (Number(item.UIUnitPrice) === 0) {
            if (isCustom) {
                this.customOptions.find(x => x.SelectedOptionId === item.SelectedOptionId).UIUnitPrice = null;
            } else {
                this.currentSelectedOptions.find(x => x.OptionId === item.OptionId).UIUnitPrice = null;
                this.allSelectedOptions.find(x => x.OptionId === item.OptionId).UIUnitPrice = null;
            }
        }
    }

    /// onBlur function that formats the unit price to 2 decimals
    setTrailingZeros(item: SelectedOption) {
        this.optionsService.setMappedSelectedOptions(this.currentSelectedOptions, this.customOptions, this.modifiedBy, this.quoteId);
        item.UIUnitPrice = Number(item.UIUnitPrice).toFixed(2).toString();
    }

    resetUnitPrice(item) {
        this.optionsService.optionsChanged$.next(true);

        let enableSaveButton: boolean = true;
        if (!item.IsIncluded && !item.IsExcluded) {
            let objIndex = this.currentSelectedOptions.findIndex(cso => cso.HomeOptionsOptionId === item.OptionId);
            if (objIndex >= 0) {
                this.currentSelectedOptions[objIndex].UIUnitPrice = this.currentSelectedOptions[objIndex].UnitPrice.toFixed(2);
                this.currentSelectedOptions[objIndex].UnitPriceOverride = null;
                this.currentSelectedOptions[objIndex].AdjustedTotalPrice =
                    Number(this.currentSelectedOptions[objIndex].UnitPrice) * this.currentSelectedOptions[objIndex].Quantity;
                this.currentSelectedOptions[objIndex].IsCustomPrice = false;
                this.currentSelectedOptions[objIndex].IsValidUnitPrice = true;
                this.currentSelectedOptions[objIndex].WasUIChanged = true;

                if (enableSaveButton) {
                    enableSaveButton = this.currentSelectedOptions[objIndex].IsValidUnitPrice;
                }

                // now update the master list
                let masterIndex = this.allSelectedOptions.findIndex(cso => cso.HomeOptionsOptionId === item.OptionId);
                if (masterIndex >= 0) {
                    this.allSelectedOptions[masterIndex].UIUnitPrice = this.currentSelectedOptions[objIndex].UIUnitPrice;
                    this.allSelectedOptions[masterIndex].UnitPriceOverride = this.currentSelectedOptions[objIndex].UnitPriceOverride;
                    this.allSelectedOptions[masterIndex].AdjustedTotalPrice = this.currentSelectedOptions[objIndex].AdjustedTotalPrice;
                    this.allSelectedOptions[masterIndex].IsCustomPrice = this.currentSelectedOptions[objIndex].IsCustomPrice;
                    this.allSelectedOptions[masterIndex].IsValidUnitPrice = this.currentSelectedOptions[objIndex].IsValidUnitPrice;
                    this.allSelectedOptions[masterIndex].WasChanged = this.currentSelectedOptions[objIndex].WasChanged;
                    this.allSelectedOptions[masterIndex].WasUIChanged = this.currentSelectedOptions[objIndex].WasUIChanged;
                }
            }
            enableSaveButton = !(this.currentSelectedOptions.filter(csO => csO.IsValidUnitPrice === false).length > 0);
        }
        this.optionsService.setMappedSelectedOptions(this.currentSelectedOptions, this.customOptions, this.modifiedBy, this.quoteId);
        this.optionsService.enableSaveButton$.next(enableSaveButton);
        this.optionsService.markOptionNotChangedByFacility(item);
    }

    disableQuantity(unitPrice: string, quantity: number): boolean {
        return (Number(unitPrice) < 0 && quantity === 1) || (Number(unitPrice) > 0 && quantity === 9999)
    }

    /// Checks for valid unit price (>= 0, <= 99999.99), sets IsCustomPrice and IsValidPrice booleans
    validateUnitPrice(item: SelectedOption, isCustom: boolean) {
        this.optionsService.optionsChanged$.next(true);
        let enableSaveButton: boolean = true;
        if (isCustom) {
            let objIndex = this.customOptions.findIndex(co => co.SelectedOptionId === item.SelectedOptionId);
            if (objIndex >= 0) {
                this.customOptions[objIndex].IsCustomPrice = true;
                if ((Number(item.UIUnitPrice) >= -99999.99) && (Number(item.UIUnitPrice) <= 99999.99)) {
                    this.customOptions[objIndex].IsValidUnitPrice = true;
                    this.customOptions[objIndex].UnitPrice = Number(item.UIUnitPrice);
                    this.customOptions[objIndex].UnitPriceOverride = null;
                    this.customOptions[objIndex].AdjustedTotalPrice = (
                        this.customOptions[objIndex].Quantity * Number(this.customOptions[objIndex].UIUnitPrice)
                    );
                    this.customOptions[objIndex].WasChanged = true;
                    this.customOptions[objIndex].WasUIChanged = true;
                } else {
                    this.customOptions[objIndex].IsValidUnitPrice = false;
                    if (enableSaveButton) {
                        enableSaveButton = false;
                    }
                }
                if (Number(this.customOptions[objIndex].UIUnitPrice) < 0 && this.customOptions[objIndex].Quantity > 0) {
                    this.customOptions[objIndex].Quantity = 1;
                    this.customOptions[objIndex].WasUIChanged = true;
                }

                // now updated the object in the master list
                let masterIndex = this.allSelectedOptions.findIndex(mo => mo.SelectedOptionId === item.SelectedOptionId);
                if (masterIndex >= 0) {
                    this.allSelectedOptions[masterIndex].IsValidUnitPrice = this.customOptions[objIndex].IsValidUnitPrice;
                    this.allSelectedOptions[masterIndex].UnitPrice = this.customOptions[objIndex].UnitPrice;
                    this.allSelectedOptions[masterIndex].UnitPriceOverride = this.customOptions[objIndex].UnitPriceOverride;
                    this.allSelectedOptions[masterIndex].AdjustedTotalPrice = this.customOptions[objIndex].AdjustedTotalPrice;
                    this.allSelectedOptions[masterIndex].Quantity = this.customOptions[objIndex].Quantity;
                    this.allSelectedOptions[masterIndex].WasChanged = this.customOptions[objIndex].WasChanged;
                    this.allSelectedOptions[masterIndex].WasUIChanged = this.customOptions[objIndex].WasUIChanged;
                }
            }

        } else {
            let objIndex = this.currentSelectedOptions.findIndex(co => co.HomeOptionsOptionId === item.HomeOptionsOptionId);
            if (objIndex >= 0) {
                if ((Number(item.UIUnitPrice) >= -99999.99) && (Number(item.UIUnitPrice) <= 99999.99)) {
                    this.currentSelectedOptions[objIndex].IsValidUnitPrice = true;
                    this.currentSelectedOptions[objIndex].UnitPriceOverride = Number(item.UIUnitPrice) !== Number(item.UnitPrice) ?
                        Number(item.UIUnitPrice) :
                        null;
                    this.currentSelectedOptions[objIndex].AdjustedTotalPrice = (
                        this.currentSelectedOptions[objIndex].Quantity * Number(this.currentSelectedOptions[objIndex].UIUnitPrice)
                    );
                    this.currentSelectedOptions[objIndex].IsCustomPrice =
                        (Number(this.currentSelectedOptions[objIndex].UIUnitPrice) !== this.currentSelectedOptions[objIndex].UnitPrice) ?
                        true :
                        false;
                    this.currentSelectedOptions[objIndex].WasChanged = true;
                    this.currentSelectedOptions[objIndex].WasUIChanged = true;
                } else {
                    this.currentSelectedOptions[objIndex].IsValidUnitPrice = false;
                    if (enableSaveButton) {
                        enableSaveButton = false;
                    }
                }
                if (Number(this.currentSelectedOptions[objIndex].UIUnitPrice) < 0 && this.currentSelectedOptions[objIndex].Quantity > 0) {
                    this.currentSelectedOptions[objIndex].Quantity = 1;
                    this.currentSelectedOptions[objIndex].WasUIChanged = true;
                }

                // now update the master list
                let masterIndex = this.allSelectedOptions.findIndex(mo => mo.HomeOptionsOptionId === item.OptionId);
                if (masterIndex >= 0) {
                    this.allSelectedOptions[masterIndex].IsValidUnitPrice = this.currentSelectedOptions[objIndex].IsValidUnitPrice;
                    this.allSelectedOptions[masterIndex].UnitPriceOverride = this.currentSelectedOptions[objIndex].UnitPriceOverride;
                    this.allSelectedOptions[masterIndex].AdjustedTotalPrice = this.currentSelectedOptions[objIndex].AdjustedTotalPrice;
                    this.allSelectedOptions[masterIndex].IsCustomPrice = this.currentSelectedOptions[objIndex].IsCustomPrice;
                    this.allSelectedOptions[masterIndex].Quantity = this.currentSelectedOptions[objIndex].Quantity;
                    this.allSelectedOptions[masterIndex].WasChanged = this.currentSelectedOptions[objIndex].WasChanged;
                    this.allSelectedOptions[masterIndex].WasUIChanged = this.currentSelectedOptions[objIndex].WasUIChanged;
                }
            }

        }
        this.optionsService.markOptionChangedByFacility(item, true);
        this.optionsService.setMappedSelectedOptions(this.currentSelectedOptions, this.customOptions, this.modifiedBy, this.quoteId);
        this.optionsService.enableSaveButton$.next(enableSaveButton);
        this.optionsService.allSelectedOptions$.next(this.allSelectedOptions);
    }

    /// Sets the current user based on which permission button was selected
    setUser(notFacility: boolean, withoutApprover: boolean, withApprover: boolean) {
        this.facilityUser = notFacility;
        this.faciltiyUserWithoutApprover = withoutApprover;
        this.facilityUserWithApprover = withApprover;
    }

    /// Determines if the Quantity is valid based on the MaxQuantity
    disableMaxQuantity(item: SelectedOption): boolean {
        if (item.MaxQuantity === 0) { return false; }
        if (item.MaxQuantity > 0 && (item.Quantity < item.MaxQuantity)) { return false; }
        return true;
    }

    /// Determines whether to disable the increment quantity button
    disableIncrementQuantity(item: SelectedOption): boolean {
        if (item.Quantity >= 9999) return true;
        if (item.Quantity == 1 && Number(item.UIUnitPrice) < 0) { return true; }
        return false;
    }

    /// Determines whether to disable the decrement quantity button
    disableDecrementQuantity(item: SelectedOption): boolean {
        if (item.Quantity <= 0) return true;
        return false;
    }

    hideCustomOption(customOptions: any): boolean {
        return (
            customOptions === undefined ||
            customOptions === null ||
            customOptions.length === 0 ||
            customOptions[0]['noMatches'] === true
            ) ?
            true :
            false;
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/option-component/option.component.ts